import React, { useRef, useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useSpreadsheetContext } from '../context/SpreadsheetContext';
import gardenBedLayouts from '../data/gardenBedLayouts.json';
import '../css/svg.css';
import { facadeImageProps } from '../lib/fenceArt';

const PreviewSVG = ({ setIsLoading: parentSetIsLoading = () => {}, styleOverride }) => {
	const { state } = useAppContext();
	const svgRef = useRef(null);
	const { plantData } = useSpreadsheetContext();
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);

	const S3_HOME_PNG_URL = import.meta.env.VITE_S3_HOME_PNGS;
	const S3_PNG_URL = import.meta.env.VITE_S3_PNG_URL;
	const {bedConfig, facade, houseSize, windows, shape, bedSize = 12, chosenPlants } = state.currentDesign || {};
	const facadeProps = facadeImageProps(S3_HOME_PNG_URL, facade, houseSize);
	const currentLayout = bedConfig ? gardenBedLayouts.find((layout) => layout.id === bedConfig) : null;

	// Facade tracking (baked per design by codegen): the fence/house + windows art follows
	// the bed's back edge — its painted base line (755, 535) is translated onto the bed's
	// back edge and scaled with projection depth. Absent for legacy designs → identity.
	const ft = currentLayout?.facadeTransform;
	const facadeGroupTransform = ft
		? `translate(0 ${ft.dy}) translate(755 535) scale(${ft.scale}) translate(-755 -535)`
		: undefined;

	const preloadImages = async () => {
		setLoadError(null);
		const imageUrls = new Set();

		// Add background image
		const backgroundUrl = `${S3_HOME_PNG_URL}/background.png`;
		imageUrls.add(backgroundUrl);

		// Add facade image
		if (facadeProps) {
			imageUrls.add(facadeProps.href);
		}

		// Add windows image
		if (windows && windows !== 'windows_none') {
			const windowsUrl = `${S3_HOME_PNG_URL}/${windows}.png`;
			imageUrls.add(windowsUrl);
		}

		// Add garden bed image
		if (shape && bedSize) {
			const bedUrl = `${S3_HOME_PNG_URL}/${shape}_${bedSize}.png`;
			imageUrls.add(bedUrl);
		}

		// Add plant images
		if (currentLayout && chosenPlants && plantData) {
			chosenPlants.forEach((chosenPlant) => {
				const plantDataItem = plantData.find(
					(plant) => plant.ID === chosenPlant.plantId
				);
				if (plantDataItem?.ImageURL) {
					const plantUrl = `${S3_PNG_URL}/${plantDataItem.ImageURL}.png`;
					imageUrls.add(plantUrl);
				}
			});
		}

		const loadImage = (url) => {
			return new Promise((resolve, reject) => {
				const img = new Image();
				img.onload = () => {
					resolve();
				};
				img.onerror = (error) => {
					console.error('Failed to load image:', url, error);
					reject(new Error(`Failed to load image: ${url}`));
				};
				img.src = url;
			});
		};

		try {
			await Promise.all(Array.from(imageUrls).map(loadImage));
		} catch (error) {
			setLoadError(error.message);
		} finally {
			setIsLoading(false);
			parentSetIsLoading(false);
		}
	};

	useEffect(() => {
		setIsLoading(true);
		preloadImages();
	}, [facade, houseSize, windows, shape, bedSize, chosenPlants, currentLayout, plantData]);

	if (isLoading) {
		return (
			<div className="preview-svg-container loading">
				<div className="loading-spinner" />
			</div>
		);
	}

	if (loadError) {
		return (
			<div className="preview-svg-container error">
				<div className="error-message">
					Error loading images: {loadError}
					<button onClick={() => preloadImages()} className="retry-button">
						Retry
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="preview-svg-container">
			<svg 
				ref={svgRef}
				xmlns='http://www.w3.org/2000/svg' 
				viewBox='0 0 1510 850' 
				preserveAspectRatio="xMidYMid meet"
				style={{ width: '100%', height: 'auto', maxWidth: '2000px', ...styleOverride }}
			>
				{/* Background - Always visible */}
				<image
					href={`${S3_HOME_PNG_URL}/background.png`}
					x='0'
					y='0'
					height='850'
					width='1510'
				/>

				{/* House-related images — grouped so the per-design facadeTransform can make
				    the fence/house track the bed's back edge, as calibrated. */}
				<g transform={facadeGroupTransform}>
				{facadeProps && (
					<image
						href={facadeProps.href}
						x={facadeProps.x}
						y={facadeProps.y}
						width={facadeProps.width}
						height={facadeProps.height}
					/>
				)}
				{windows && windows !== 'windows_none' && (
					<image
						href={`${S3_HOME_PNG_URL}/${windows}.png`}
						x='0'
						y='0'
						width='1510'
						height='850'
					/>
				)}
				</g>

				{/* Garden bed-related images */}
				{shape && bedSize && (
					<image
						href={`${S3_HOME_PNG_URL}/${shape}_${bedSize}.png`}
						x='0'
						y='0'
						width='1510'
						height='850'
					/>
				)}

				{/* Render plants if data is available */}
				{currentLayout &&
					chosenPlants?.flatMap((chosenPlant) => {
						if (!plantData) {
							console.warn('Plant data is not available.');
							return [];
						}

						const plantDataItem = plantData.find(
							(plant) => plant.ID === chosenPlant.plantId
						);
						if (!plantDataItem) {
							console.warn(`No data found for plantId: ${chosenPlant.plantId}`);
							return [];
						}

						const imageURL = plantDataItem.ImageURL;

						const layoutPlant = currentLayout.plants.find(
							(plant) => plant.category === chosenPlant.category
						);

						if (!layoutPlant || !imageURL) return [];

						const image = new Image();
						image.src = `${S3_PNG_URL}/${imageURL}.png`;

						const imageHeight = image.height;
						const imageWidth = image.width;

						return layoutPlant.coordinates.map((coord, index) => ({
							category: chosenPlant.category,
							imageURL,
							imageWidth,
							imageHeight,
							coord,
							index,
						}));
					})
					// Painter's algorithm: SVG paints in document order, so sprites must be
					// emitted back-to-front (ascending y) ACROSS categories — otherwise a far
					// plant from a later category paints over a near plant from an earlier one.
					.sort((a, b) => a.coord.y - b.coord.y)
					.map(({ category, imageURL, imageWidth, imageHeight, coord, index }) => {
						// Perspective-true sprite size: s baked per-coordinate by codegen (1 for legacy entries).
						const spriteScale = 1.3 * (coord.s ?? 1);
						const scaledWidth = imageWidth * spriteScale;
						const scaledHeight = imageHeight * spriteScale;
						return (
							<image
								key={`${category}-${index}`}
								href={`${S3_PNG_URL}/${imageURL}.png`}
								x={coord.x - scaledWidth / 2}
								y={coord.y - scaledHeight}
								width={scaledWidth}
								height={scaledHeight}
							/>
						);
					})}
			</svg>
		</div>
	);
};

export default PreviewSVG;
